/** 
 * @file useTaskMutationActions.js
 * 
 * @module useTaskMutationActions
 * 
 * @description
 * Custom hook that consolidates multiple task mutation actions (create, delete, move, update).
 * 
 * This module also contains a private helper function, `createTaskAction`, which abstracts the 
 * process of extracting the `mutate` function and status (data, error, loading) from individual
 * task mutation hooks.
 * 
 * The main hook, `useTaskMutationActions`, combines the functionality of the task-related hooks
 * and provides an easy-to-use API for performing these mutations in your components.
 * 
 * @see {@link ./useCreateTask.js} for task creation logic.
 * @see {@link ./useDeleteTask.js} for task deletion logic.
 * @see {@link ./useMoveTask.js} for task moving logic.
 * @see {@link ./useUpdateTask.js} for task update logic.
 */

import { useMemo } from "react";
import { useCreateTask } from "./useCreateTask";
import { useDeleteTask } from "./useDeleteTask";
import { useMoveTask } from "./useMoveTask";
import { useUpdateTask } from "./useUpdateTask";

/**
 * @private
 * Custom hook to extract mutate function and status (data, error, loading) from a task hook.
 * 
 * @param {function} useTaskHook - Hook providing mutation and status.
 * @returns {Object} Object with mutate function and status (data, error, loading).
 */
const useCreateTaskAction = (useTaskHook) => {
  const [ mutate, {data, error, loading }] = useTaskHook();
  return {
    mutate,
    status: {
      data,
      error,
      loading,
    },
  };
};

/**
 * Custom hook to handle multiple task mutation actions (create, delete, move, update).
 * 
 * @returns {Object} An object containing the task mutation actions and their corresponding status.
 */
const useTaskMutationActions = () => {
  const createTask = useCreateTaskAction(useCreateTask);
  const deleteTask = useCreateTaskAction(useDeleteTask);
  const moveTask = useCreateTaskAction(useMoveTask);
  const updateTask = useCreateTaskAction(useUpdateTask);

  const isLoading = createTask.status.loading || deleteTask.status.loading || moveTask.status.loading || updateTask.status.loading;

  // Memoize the return value to avoid unnecessary recalculations
  return useMemo(() => ({
    createTask,
    deleteTask,
    moveTask,
    updateTask,
    loading: isLoading,
  }), [
    createTask,
    deleteTask,
    moveTask,
    updateTask,
    isLoading,
  ]);
};

export { useTaskMutationActions };
